import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";

import { columnType, optionLabel, optionValue } from "./columnValues";
import {
  type FilterOperatorDef,
  findOperator,
  operatorsFor,
} from "./filterOperators";
import type { DataTableColDef, FilterItem, FilterModel } from "./types";

const VALUE_DEBOUNCE_MS = 500;
const INPUT_TYPE = {
  string: "text",
  number: "number",
  date: "date",
  singleSelect: "text",
  boolean: "text",
  actions: "text",
} as const;

interface ValueInputProps<R> {
  column: DataTableColDef<R>;
  operator: FilterOperatorDef;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ValueInput<R>({
  column,
  operator,
  value,
  onChange,
}: ValueInputProps<R>) {
  const type = columnType(column);
  const [draft, setDraft] = useState<string>(
    value === undefined || value === null ? "" : String(value),
  );

  useEffect(() => {
    setDraft(value === undefined || value === null ? "" : String(value));
  }, [value]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new draft should restart the debounce timer
  useEffect(() => {
    if (
      operator.input !== "single" ||
      type === "singleSelect" ||
      type === "boolean"
    )
      return undefined;
    const current = value === undefined || value === null ? "" : String(value);
    if (draft === current) return undefined;
    const timer = window.setTimeout(() => {
      if (draft === "") onChange(undefined);
      else onChange(type === "number" ? Number(draft) : draft);
    }, VALUE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  if (operator.input === "none") {
    return <Box sx={{ width: 190 }} />;
  }

  if (operator.input === "multi") {
    const options = (column.valueOptions ?? []).map(optionValue);
    const values = Array.isArray(value) ? (value as string[]) : [];
    return (
      <Autocomplete
        multiple
        freeSolo={type !== "singleSelect"}
        options={options}
        value={values}
        getOptionLabel={(option) => {
          const match = column.valueOptions?.find(
            (candidate) => optionValue(candidate) === option,
          );
          return match ? optionLabel(match) : String(option);
        }}
        onChange={(_event, next) =>
          onChange(type === "number" ? next.map(Number) : next)
        }
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((tag, index) => (
            <Chip
              size="small"
              label={tag}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getTagProps({ index })}
              key={String(tag)}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            variant="standard"
            label="Value"
            placeholder="Filter value"
          />
        )}
        sx={{ width: 190 }}
      />
    );
  }

  if (type === "singleSelect" || type === "boolean") {
    return (
      <TextField
        select
        variant="standard"
        label="Value"
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value === "" ? undefined : event.target.value)
        }
        sx={{ width: 190 }}
      >
        <MenuItem value="">&nbsp;</MenuItem>
        {(column.valueOptions ?? []).map((option) => (
          <MenuItem key={optionValue(option)} value={optionValue(option)}>
            {optionLabel(option)}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  return (
    <TextField
      variant="standard"
      label="Value"
      placeholder="Filter value"
      type={INPUT_TYPE[type]}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      InputLabelProps={{ shrink: true }}
      sx={{ width: 190 }}
    />
  );
}

interface FilterPanelProps<R> {
  columns: DataTableColDef<R>[];
  filterModel: FilterModel;
  onFilterModelChange: (model: FilterModel) => void;
}

export function newFilterItem<R>(
  column: DataTableColDef<R>,
  id: number,
): FilterItem {
  return {
    id,
    field: column.field,
    operator: operatorsFor(column)[0].value,
  };
}

export function nextFilterId(model: FilterModel): number {
  return model.items.reduce((max, item) => Math.max(max, item.id), 0) + 1;
}

export default function FilterPanel<R>({
  columns,
  filterModel,
  onFilterModelChange,
}: FilterPanelProps<R>) {
  const filterable = columns.filter((column) => column.filterable !== false);
  const byField = new Map(filterable.map((column) => [column.field, column]));
  const logicOperator = filterModel.logicOperator ?? "and";

  const updateItem = (id: number, patch: Partial<FilterItem>) =>
    onFilterModelChange({
      ...filterModel,
      items: filterModel.items.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    });

  const removeItem = (id: number) =>
    onFilterModelChange({
      ...filterModel,
      items: filterModel.items.filter((item) => item.id !== id),
    });

  const addItem = () => {
    if (!filterable.length) return;
    onFilterModelChange({
      ...filterModel,
      items: [
        ...filterModel.items,
        newFilterItem(filterable[0], nextFilterId(filterModel)),
      ],
    });
  };

  return (
    <Box sx={{ padding: "8px 8px 4px", minWidth: 560 }}>
      {filterModel.items.map((item, index) => {
        const column = byField.get(item.field) ?? filterable[0];
        if (!column) return null;
        const operator =
          findOperator(column, item.operator) ?? operatorsFor(column)[0];
        return (
          <Box
            key={item.id}
            role="group"
            aria-label="Filter condition"
            sx={{ display: "flex", alignItems: "flex-end", gap: 1, mb: 1 }}
          >
            <IconButton
              size="small"
              aria-label="Delete"
              onClick={() => removeItem(item.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box sx={{ width: 70 }}>
              {index > 0 && (
                <TextField
                  select
                  variant="standard"
                  label="Operator"
                  value={logicOperator}
                  disabled={index > 1}
                  onChange={(event) =>
                    onFilterModelChange({
                      ...filterModel,
                      logicOperator: event.target.value as "and" | "or",
                    })
                  }
                  fullWidth
                  inputProps={{ "aria-label": "Logic operator" }}
                >
                  <MenuItem value="and">And</MenuItem>
                  <MenuItem value="or">Or</MenuItem>
                </TextField>
              )}
            </Box>
            <TextField
              select
              variant="standard"
              label="Columns"
              value={column.field}
              onChange={(event) => {
                const nextColumn = byField.get(event.target.value);
                if (!nextColumn) return;
                const keepsOperator = findOperator(nextColumn, item.operator);
                updateItem(item.id, {
                  field: nextColumn.field,
                  operator: keepsOperator
                    ? item.operator
                    : operatorsFor(nextColumn)[0].value,
                  value: undefined,
                });
              }}
              sx={{ width: 150 }}
            >
              {filterable.map((option) => (
                <MenuItem key={option.field} value={option.field}>
                  {option.headerName ?? option.field}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              variant="standard"
              label="Operator"
              value={operator.value}
              onChange={(event) => {
                const nextOperator = findOperator(column, event.target.value);
                if (!nextOperator) return;
                updateItem(item.id, {
                  operator: nextOperator.value,
                  value:
                    nextOperator.input === operator.input
                      ? item.value
                      : undefined,
                });
              }}
              sx={{ width: 120 }}
            >
              {operatorsFor(column).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <ValueInput
              column={column}
              operator={operator}
              value={item.value}
              onChange={(value) => updateItem(item.id, { value })}
            />
          </Box>
        );
      })}
      <Box sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={addItem}>
          Add filter
        </Button>
        {filterModel.items.length > 0 && (
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => onFilterModelChange({ ...filterModel, items: [] })}
          >
            Remove all
          </Button>
        )}
      </Box>
    </Box>
  );
}
